import { useState, useCallback, useEffect } from 'react';
import { useCompetitionAnswer } from '@hooks/useCompetitionAnswer';
import { CompetitionAnswerStatus, CompetitionQuestion } from './Competition.types';

interface UseCompetitionProps {
  competitionId: string;
  questions: CompetitionQuestion[];
  requireTermsApproval: boolean;
  answerStatus?: CompetitionAnswerStatus;
}

export const useCompetition = ({
  competitionId,
  questions,
  requireTermsApproval,
  answerStatus,
}: UseCompetitionProps) => {
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isFreshVote, setIsFreshVote] = useState(false);

  const { submitEntry, isLoading: isSubmitting, isCorrect } = useCompetitionAnswer();

  const handleAnswerSelect = useCallback((questionId: string, answerId: string) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: answerId,
    }));
  }, []);

  const handleTermsToggle = useCallback(() => {
    setTermsAccepted((prev) => !prev);
  }, []);

  const isFormValid = questions.every((q) => selectedAnswers[q.key]) && (!requireTermsApproval || termsAccepted);

  const handleSubmit = useCallback(async () => {
    if (!isFormValid) return;
    await submitEntry(competitionId, selectedAnswers);
    setIsFreshVote(true);
  }, [competitionId, selectedAnswers, isFormValid, submitEntry]);

  useEffect(() => {
    if (answerStatus?.questionsAnswered) {
      setSelectedAnswers(
        Object.fromEntries(answerStatus.questionsAnswered.map((item) => [item.questionKey, item.answerKey])),
      );
    }
  }, [answerStatus]);

  return {
    isFreshVote,
    isSubmitting,
    isCorrect,
    selectedAnswers,
    termsAccepted,
    handleAnswerSelect,
    handleTermsToggle,
    handleSubmit,
    isFormValid,
  };
};
